import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import $ from 'jquery';
import { SiaI18nService } from '../services/i18n.service';
import { SiaSizeDetectorService } from '../services/size-detector.service';

@Directive({
  selector: '[siaTooltip]',
  standalone: true,
})
export class SiaTooltipDirective implements OnDestroy, AfterViewInit {
  // 1. Migrate Inputs to Signal Inputs (use model for bidirectional binding)
  tooltipText = model<string | undefined>('', { alias: 'siaTooltip' }); // Writable signal for dynamic updates
  tooltipDelay = input<number>(300);
  longPressDelay = input<number>(500);
  fixedX = input<number>(50); // Fixed X coordinate in pixels (only used when position is 'fixed')
  fixedY = input<number>(50); // Fixed Y coordinate in pixels (only used when position is 'fixed')
  position = input<'top' | 'bottom' | 'left' | 'right' | 'fixed'>('bottom');
  disable = input<boolean>(false); // Disable tooltip functionality
  disableMobile = input<boolean>(false); // Disable tooltip on mobile devices
  disableTranslation = input<boolean>(false); // Disable translation of tooltip text

  #tooltipElement: HTMLElement | null = null;
  #showTimeout: any;
  #hideTimeout: any;
  #isMobileDevice: boolean = false;
  #isMobileForDisable?: boolean | null = false;
  #touchStartX: number = 0;
  #touchStartY: number = 0;
  #i18nService = inject(SiaI18nService);

  globalClickListener?: (event: Event) => void;

  #sizeDetector = inject(SiaSizeDetectorService);

  #boundOnTouchStart = (event: TouchEvent) => this.onTouchStart(event);
  #boundOnTouchEnd = (event: TouchEvent) => this.onTouchEnd(event);

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.el.nativeElement.addEventListener('touchstart', this.#boundOnTouchStart, {
      passive: true,
    });
    this.el.nativeElement.addEventListener('touchend', this.#boundOnTouchEnd, {
      passive: true,
    });
  }

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent) {
    // Only handle mouse events on non-mobile devices
    this.#isMobileDevice = !this.#sizeDetector.isHoverable;
    if (this.#isMobileDevice) {
      return;
    }

    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
    }

    this.#showTimeout = setTimeout(() => {
      this.showTooltip();
    }, this.tooltipDelay());
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent) {
    // Only handle mouse events on non-mobile devices
    if (this.#isMobileDevice) {
      return;
    }

    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
    }

    this.#hideTimeout = setTimeout(() => {
      this.hideTooltip();
    }, 100);
  }

  // Touch events for mobile devices - tap to toggle
  onTouchStart(event: TouchEvent) {
    this.#isMobileDevice = !this.#sizeDetector.isHoverable;

    if (!this.#isMobileDevice) {
      return;
    }

    if (event.touches.length > 0) {
      this.#touchStartX = event.touches[0].clientX;
      this.#touchStartY = event.touches[0].clientY;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.#isMobileDevice) {
      return;
    }

    // Check if it was a scroll (movement > 10px)
    if (event.changedTouches.length > 0) {
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      const diffX = Math.abs(touchEndX - this.#touchStartX);
      const diffY = Math.abs(touchEndY - this.#touchStartY);

      if (diffX > 10 || diffY > 10) {
        return; // It was a scroll/drag, not a tap
      }
    }

    // Toggle tooltip on tap
    if (this.#tooltipElement) {
      this.hideTooltip();
    } else {
      this.showTooltip();
      // Auto-hide after a few seconds on mobile
      this.#hideTimeout = setTimeout(() => {
        this.hideTooltip();
      }, 3000);
    }
  }

  // For desktop, use click event as well
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.#isMobileDevice) {
      return; // Mobile uses touch events
    }

    // Toggle tooltip on click for desktop
    if (this.#tooltipElement) {
      this.hideTooltip();
    } else {
      this.showTooltip();
      // Auto-hide after a few seconds on desktop too
      this.#hideTimeout = setTimeout(() => {
        this.hideTooltip();
      }, 3000);
    }
  }

  private showTooltip() {
    if (!this.tooltipText()) {
      return;
    }
    this.#isMobileForDisable = this.#sizeDetector.currentSize.isMobile;
    if (
      !this.tooltipText() ||
      this.#tooltipElement ||
      this.disable() ||
      (this.disableMobile() && this.#isMobileForDisable)
    ) {
      return;
    }

    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
    }

    // Create tooltip element
    this.#tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.#tooltipElement, 'sia-tooltip');
    // Promote to the browser top-layer so the tooltip sits above any CDK dialog
    // (which itself uses popover="manual" on modern browsers and beats every z-index).
    this.renderer.setAttribute(this.#tooltipElement, 'popover', 'manual');
    this.renderer.setProperty(
      this.#tooltipElement,
      'innerHTML',
      this.disableTranslation() ? this.tooltipText() : this.#i18nService.translate(this.tooltipText()!),
    );

    // Add tooltip styles using CSS variables
    this.renderer.setStyle(this.#tooltipElement, 'position', this.position() === 'fixed' ? 'fixed' : 'absolute');
    // Neutralize the UA default popover styles (margin:auto, inset:0, border, etc.)
    this.renderer.setStyle(this.#tooltipElement, 'margin', '0');
    this.renderer.setStyle(this.#tooltipElement, 'inset', 'auto');
    this.renderer.setStyle(this.#tooltipElement, 'border', '0');
    this.renderer.setStyle(this.#tooltipElement, 'overflow', 'visible');
    this.renderer.setStyle(this.#tooltipElement, 'background-color', 'var(--mat-sys-on-surface, #333)');
    this.renderer.setStyle(this.#tooltipElement, 'color', 'var(--mat-sys-surface, #fff)');
    this.renderer.setStyle(this.#tooltipElement, 'padding', '4px 12px');
    this.renderer.setStyle(this.#tooltipElement, 'border-radius', '8px');
    this.renderer.setStyle(
      this.#tooltipElement,
      'font-family',
      'Roboto, "Helvetica Neue Light", "Helvetica Neue", Helvetica, Arial',
    );
    this.renderer.setStyle(this.#tooltipElement, 'font-size', '12px');
    // Allow wrapping so max-width can take effect
    this.renderer.setStyle(this.#tooltipElement, 'white-space', 'normal');
    this.renderer.setStyle(this.#tooltipElement, 'z-index', '9999');
    this.renderer.setStyle(this.#tooltipElement, 'pointer-events', 'none');
    this.renderer.setStyle(this.#tooltipElement, 'opacity', '0');

    // Set max-width to the larger of 20% of the viewport width and 200px
    // Use CSS to pick the larger of 20vw and 200px
    this.renderer.setStyle(this.#tooltipElement, 'max-width', 'max(10vw, 250px)');
    this.renderer.setStyle(this.#tooltipElement, 'overflow-wrap', 'break-word');

    // Set max-width to the larger of 20% of the viewport width and 200px
    // Use CSS to pick the larger of 20vw and 200px
    this.renderer.setStyle(this.#tooltipElement, 'max-width', 'max(10vw, 250px)');
    this.renderer.setStyle(this.#tooltipElement, 'overflow-wrap', 'break-word');

    // Append to body
    this.renderer.appendChild(document.body, this.#tooltipElement);

    // Promote to the top layer (above any popover-based CDK overlay).
    // showPopover() is a no-op if the browser doesn't support the Popover API.
    try {
      (this.#tooltipElement as HTMLElement & { showPopover?: () => void }).showPopover?.();
    } catch {
      // ignore: not supported or already showing
    }

    // Position tooltip based on position input
    this.positionTooltip();

    // Show tooltip with fade-in effect using jQuery
    $(this.#tooltipElement!).animate({ opacity: 1 }, 300);

    // Add global click listener to hide tooltip when clicking outside
    this.globalClickListener = (event: Event) => {
      const target = event.target as HTMLElement;
      if (this.#tooltipElement && !this.#tooltipElement.contains(target) && !this.el.nativeElement.contains(target)) {
        this.hideTooltip();
      }
    };
    document.addEventListener('click', this.globalClickListener, {
      passive: true,
    });
  }

  private positionTooltip() {
    if (!this.#tooltipElement) {
      return;
    }

    if (this.position() === 'fixed') {
      // Use fixed positioning with custom coordinates
      this.renderer.setStyle(this.#tooltipElement, 'top', `${this.fixedY()}px`);
      this.renderer.setStyle(this.#tooltipElement, 'left', `${this.fixedX()}px`);
      return;
    }

    // Get element and tooltip dimensions using jQuery
    const $target = $(this.el.nativeElement);
    const $tooltip = $(this.#tooltipElement);
    const targetOffset = $target.offset();
    const targetWidth = $target.outerWidth() || 0;
    const targetHeight = $target.outerHeight() || 0;
    const tooltipWidth = $tooltip.outerWidth() || 0;
    const tooltipHeight = $tooltip.outerHeight() || 0;

    if (!targetOffset) {
      return;
    }

    let top = 0;
    let left = 0;

    switch (this.position()) {
      case 'top':
        top = targetOffset.top - tooltipHeight - 8;
        left = targetOffset.left + targetWidth / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = targetOffset.top + targetHeight + 8;
        left = targetOffset.left + targetWidth / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = targetOffset.top + targetHeight / 2 - tooltipHeight / 2;
        left = targetOffset.left - tooltipWidth - 8;
        break;
      case 'right':
        top = targetOffset.top + targetHeight / 2 - tooltipHeight / 2;
        left = targetOffset.left + targetWidth + 8;
        break;
    }

    // Ensure tooltip stays within viewport
    const windowWidth = $(window).width() || 0;
    const windowHeight = $(window).height() || 0;

    if (left < 8) {
      left = 8;
    } else if (left + tooltipWidth > windowWidth - 8) {
      left = windowWidth - tooltipWidth - 8;
    }

    if (top < 8) {
      top = 8;
    } else if (top + tooltipHeight > windowHeight - 8) {
      top = windowHeight - tooltipHeight - 8;
    }

    this.renderer.setStyle(this.#tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.#tooltipElement, 'left', `${left}px`);
  }

  private hideTooltip() {
    // Always remove global click listener if it exists
    if (this.globalClickListener) {
      document.removeEventListener('click', this.globalClickListener);
      this.globalClickListener = undefined;
    }

    if (!this.#tooltipElement) {
      return;
    }

    // Stop any ongoing animations to avoid queued callbacks holding references
    $(this.#tooltipElement).stop(true, true);

    // Hide tooltip with fade-out effect using jQuery
    $(this.#tooltipElement!).animate({ opacity: 0 }, 300, () => {
      if (this.#tooltipElement) {
        // Remove only if the element is still attached to a parent
        const parent = this.#tooltipElement.parentNode;
        if (parent) {
          this.renderer.removeChild(parent, this.#tooltipElement);
        }
        this.#tooltipElement = null;
      }
    });
  }

  ngOnDestroy() {
    this.el.nativeElement.removeEventListener('touchstart', this.#boundOnTouchStart);
    this.el.nativeElement.removeEventListener('touchend', this.#boundOnTouchEnd);

    if (this.#showTimeout) {
      clearTimeout(this.#showTimeout);
    }
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
    }

    // Remove global click listener even if tooltip element is already gone
    if (this.globalClickListener) {
      document.removeEventListener('click', this.globalClickListener);
      this.globalClickListener = undefined;
    }

    // Synchronously remove tooltip without animation to prevent dangling callbacks
    if (this.#tooltipElement) {
      $(this.#tooltipElement).stop(true, true);
      if (this.#tooltipElement?.parentNode) {
        const parent = this.#tooltipElement.parentNode;
        this.renderer.removeChild(parent, this.#tooltipElement);
      }
      this.#tooltipElement = null;
    }
  }
}
